import { useMemo, useState } from 'react';

import { LocalTime } from '~/components/common/LocalTime';
import { Select } from '~/components/form';
import { ListToolbar, PageHeader, PageShell } from '~/components/layout';
import TemplateStatusChip from '~/components/pages/notifications/templates/TemplateStatusChip';
import {
  Badge,
  Card,
  CardBody,
  type DataTableColumn,
  DataTable,
  FilterDrawer,
  Pagination,
  TagsCell,
} from '~/components/ui';
import { useListUrlState } from '~/hooks/useListUrlState';
import { errorText } from '~/lib/apiError';
import { clampPage, pageCount } from '~/lib/listParams';
import {
  CHANNEL_LABELS,
  GLOBAL_CUSTOMER,
  matchesTemplateFilters,
  STATUS_FILTER_OPTIONS,
  templateChannels,
} from '~/lib/notification/templateForm';
import { useListTemplatesQuery } from '~/modules/query/notification/templates';
import type { Template } from '~/modules/query/notification/types';
import { routes } from '~/app/routes';

const FILTER_KEYS = ['customerId', 'status'] as const;

const dash = <span className="text-gray-400 dark:text-gray-500">—</span>;

export default function TemplatesPage() {
  const list = useListUrlState({ filterKeys: FILTER_KEYS });
  const templates = useListTemplatesQuery();
  const [showFilters, setShowFilters] = useState(false);

  const all = useMemo(() => templates.data ?? [], [templates.data]);

  const customerOptions = useMemo(() => {
    const ids = [...new Set(all.flatMap((t) => t.customerIds))].sort();
    return [
      { value: GLOBAL_CUSTOMER, label: 'No customer (global)' },
      ...ids.map((id) => ({ value: id, label: id })),
    ];
  }, [all]);

  const rows = useMemo(
    () =>
      all.filter((template) =>
        matchesTemplateFilters(template, {
          q: list.q,
          customerId: list.filters.customerId,
          status: list.filters.status,
        }),
      ),
    [all, list.q, list.filters.customerId, list.filters.status],
  );

  const pages = pageCount(rows.length, list.pageSize);
  const page = clampPage(list.page, rows.length, list.pageSize);
  const pageRows = rows.slice((page - 1) * list.pageSize, page * list.pageSize);
  const filtered = Boolean(list.q) || list.filterCount > 0;

  const columns: DataTableColumn<Template>[] = [
    {
      id: 'name',
      header: 'Name',
      cell: (template) => (
        <>
          <p className="font-medium text-gray-800 dark:text-white/90">{template.name}</p>
          <p className="font-mono text-xs text-gray-500 dark:text-gray-400">
            {template.templateKey}
          </p>
        </>
      ),
    },
    {
      id: 'customers',
      header: 'Customers',
      cell: (template) => <TagsCell tags={template.customerIds} />,
    },
    {
      id: 'channels',
      header: 'Channels',
      cell: (template) => (
        <div className="flex flex-wrap gap-1">
          {templateChannels(template).map((channel) => (
            <Badge key={channel} color="gray">
              {CHANNEL_LABELS[channel]}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      id: 'version',
      header: 'Version',
      cell: (template) => (template.version === null ? dash : `v${template.version}`),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (template) => (
        <TemplateStatusChip latest={template} testId={`templates-status-${template.id}`} />
      ),
    },
    {
      id: 'updated',
      header: 'Updated',
      cell: (template) => <LocalTime value={template.updatedAt} format="date" />,
    },
  ];

  return (
    <PageShell>
      <PageHeader
        title="Templates"
        description="The messages notifications send, written once per channel and published by version."
        breadcrumbs={[{ label: 'Notifications' }, { label: 'Templates' }]}
        actions={[
          {
            label: 'Create template',
            kind: 'primary',
            href: routes.notificationTemplateNew,
            testId: 'templates-new-button',
          },
        ]}
      />

      <Card>
        <ListToolbar
          title="Templates"
          search={{
            value: list.q,
            onChange: list.setQ,
            placeholder: 'Search by name or key…',
            debounceMs: 0,
            testId: 'templates-search',
          }}
          filters={{
            count: list.filterCount,
            onClick: () => setShowFilters(true),
            onClear: list.clear,
            testId: 'templates-filter-button',
          }}
        />
        <CardBody className="p-0">
          <DataTable<Template>
            framed={false}
            testId="templates-table"
            loading={!templates.data && !templates.error}
            refreshing={templates.isFetching && !!templates.data}
            data={pageRows}
            rowKey={(template) => template.id}
            href={(template) => routes.notificationTemplate(template.id)}
            rowLabel={(template) => `Open ${template.name}`}
            columns={columns}
            error={
              templates.error && !templates.data
                ? {
                    what: 'the templates',
                    message: errorText(templates.error, 'The templates could not be loaded.'),
                    onRetry: () => templates.refetch(),
                  }
                : undefined
            }
            emptyMessage={filtered ? 'No templates match.' : 'No templates yet.'}
            emptyDescription={
              filtered
                ? 'Clear the search or the filters to see every template.'
                : 'Create a template to write a message once and send it by key.'
            }
          />
        </CardBody>
        <Pagination
          pageIndex={page - 1}
          pageSize={list.pageSize}
          pageCount={pages}
          totalRows={rows.length}
          onPageChange={(index) => list.setPage(index + 1)}
          pageSizeControl={{
            value: list.pageSize,
            onChange: list.setPageSize,
            options: [...list.pageSizes],
          }}
        />
      </Card>

      <FilterDrawer
        open={showFilters}
        onClose={() => setShowFilters(false)}
        value={list.filters}
        onApply={list.setFilters}
        onClear={list.clear}
        testId="templates-filter-drawer"
      >
        {(draft, patch) => (
          <>
            <Select
              id="templates-filter-customerId"
              label="Customer"
              placeholder="All customers"
              placeholderSelectable
              value={draft.customerId}
              onChange={(customerId) => patch({ customerId })}
              options={customerOptions}
            />
            <Select
              id="templates-filter-status"
              label="Status"
              placeholder="Any status"
              placeholderSelectable
              value={draft.status}
              onChange={(status) => patch({ status })}
              options={STATUS_FILTER_OPTIONS}
            />
          </>
        )}
      </FilterDrawer>
    </PageShell>
  );
}
